import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ErrorMessagesPipe } from '../../pipes/error-messages.pipe';


@Component({
  selector: 'app-text-input',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    ErrorMessagesPipe,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true
    }
  ],
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.css',
})
export class TextInputComponent {
  @Input() label = '';
  @Input() control: FormControl = new FormControl();
  @Input() placeholder?: string;
  @Input() hint?: string;
  @Input() showErrors: boolean = false;
  @Input() value?: string;
  // @Input() messages = validationMessages;
  @Input() disabled: boolean = false;
  @Input() isViewMode: boolean = false;
  errorMessage: string | null = null; // Property to hold the error message
  @Input() applyFocusEvent: boolean = false;
  @Input() min: string | null = null;
  @Input() max: string | null = null;
  @Input() digitOnly: boolean = false;
  @Input() alphaOnly: boolean = false;
  @Input() maxLength: number | null = null;
  @Input() inputMode?: string;

  @Output() blur = new EventEmitter<Event>();
  @Output() valueChange = new EventEmitter<string>();
  @Output() change = new EventEmitter<any>();
  @Output() keyupEvent = new EventEmitter<KeyboardEvent>();
  @Input() type: string = 'text';
  // @Input() duplicateItemNumErr: boolean = false;
  @Input() duplicateItem: any;


  constructor() { }

  ngOnInit() {
    this.syncDisabledState();
  }

  ngOnChanges() {
    this.errorMessage = '';
    this.syncDisabledState();
  }

  private syncDisabledState(): void {
    if (!this.control) return;
    if (this.disabled && !this.control.disabled) {
      this.control.disable({ emitEvent: false });
    } else if (!this.disabled && this.control.disabled) {
      this.control.enable({ emitEvent: false });
    }
  }

  getControlName(control: FormControl) {
    // return this.formUtils.getControlName(control);
  }

  handleBlur(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (this.control) {
      const trimmedValue = inputElement.value.trim();
      this.control.setValue(trimmedValue, { emitEvent: false });
      this.valueChange.emit(trimmedValue);
    }
    this.blur.emit(event);
  }


  formatInputValue(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    let numericValue = parseFloat(inputElement.value);

    if (!isNaN(numericValue)) {
      numericValue = parseFloat((numericValue * 1.0).toFixed(1));

      // Limit to a maximum of 100
      if (isNaN(numericValue)) {
        // Clear the error when input is cleared or not a number
        this.showError('');
      } else if (numericValue > 100) {
        this.showError('Value cannot exceed 100');
        numericValue = 100.0;
      } else {
        // If the value is valid, clear any existing error
        this.showError('');
      }

      inputElement.value = numericValue.toFixed(1); // Update the input value
      this.control.setValue(numericValue.toFixed(1)); // Update the FormControl value
      this.valueChange.emit(numericValue.toFixed(1)); // Emit the formatted value
    }
  }
  private showError(message: string) {
    this.errorMessage = message; //
  }

  onChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.value = inputElement.value;
    this.valueChange.emit(this.value);
    this.change.emit(event);
    
    // Trigger validation on input change
    if (this.control) {
      this.control.updateValueAndValidity();
      this.control.markAsTouched();
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    this.keyupEvent.emit(event);
  }

  onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const original = inputElement.value || '';
    let sanitized = original;

    if (this.digitOnly) {
      sanitized = original.replace(/[^0-9]/g, '');
      if (this.maxLength && sanitized.length > this.maxLength) {
        sanitized = sanitized.slice(0, this.maxLength);
      }
    } else if (this.alphaOnly) {
      sanitized = original.replace(/[^A-Za-z\s']/g, '');
      // Prevent multiple consecutive spaces
      sanitized = sanitized.replace(/\s{2,}/g, ' ');
      // Trim leading space
      sanitized = sanitized.trimStart();
      if (this.maxLength && sanitized.length > this.maxLength) {
        sanitized = sanitized.slice(0, this.maxLength);
      }
    }

    if (sanitized !== original) {
      inputElement.value = sanitized;
    }
    if (this.control) {
      this.control.setValue(sanitized, { emitEvent: true });
      this.control.updateValueAndValidity({ emitEvent: true });
    }
  }

  isFieldRequired(control: AbstractControl | null): boolean {
    if (!control) {
      return false;
    }

    return control.hasValidator(Validators.required);
  }
}
